import { portfolioFetcherDaily } from './portfolioFetcher'
import { EthAddr } from '@shared/SchemaTypes'
import dayjs from 'dayjs'

interface OptimizedAPYData {
  timestamp: number
  value: number
  month: string
}

const aggregateToWeekly = (dailyData: Array<{ timestamp: number; value: number }>) => {
  if (dailyData.length === 0) return []

  const weeklyData: Array<{ timestamp: number; value: number; month: string }> = []

  for (let i = 0; i < dailyData.length; i += 7) {
    const weekData = dailyData.slice(i, i + 7).filter((item) => !isNaN(item.value) && item.value !== null)
    if (weekData.length > 0) {
      const weekTimestamp = weekData[Math.floor(weekData.length / 2)].timestamp
      const validValues = weekData.filter((item) => item.value !== 0 || weekData.every((w) => w.value === 0))
      const weekValue =
        validValues.length > 0
          ? validValues.reduce((sum, item) => sum + item.value, 0) / validValues.length
          : weekData.reduce((sum, item) => sum + item.value, 0) / weekData.length

      weeklyData.push({
        timestamp: weekTimestamp,
        value: weekValue,
        month: dayjs(weekTimestamp * 1000).format('MMM YY'),
      })
    }
  }
  return weeklyData
}

export const fetchOptimizedAPYDataForSierra = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  timeFilter: '1M' | '3M' | '6M' | '1Y',
  accountAddr?: EthAddr
): Promise<OptimizedAPYData[]> => {
  try {
    const endDate = new Date()
    const startDate = new Date()

    switch (timeFilter) {
      case '1M':
        startDate.setMonth(endDate.getMonth() - 1)
        break
      case '3M':
        startDate.setMonth(endDate.getMonth() - 3)
        break
      case '6M':
        startDate.setMonth(endDate.getMonth() - 6)
        break
      case '1Y':
        startDate.setFullYear(endDate.getFullYear() - 1)
        break
    }

    const { portfolioCTOs } = await portfolioFetcherDaily(
      serverApiBaseUrl,
      restAPIKey,
      accountAddr,
      'MUSDC',
      startDate,
      endDate,
      0,
      1000,
      poolAddr
    )

    if (portfolioCTOs && portfolioCTOs.length > 0) {
      const apyData = portfolioCTOs
        .map((portfolio) => ({
          timestamp: portfolio.timestamp,
          value: Number(portfolio.portfolioSummaryExcludingAccount.indicativeInterestRate) / 1e18,
        }))
        .filter((item) => item.timestamp && !isNaN(item.value) && item.value !== null)
        .sort((a, b) => a.timestamp - b.timestamp)

      if (apyData.length > 0) {
        return aggregateToWeekly(apyData)
      }
    }
  } catch (error) {
    console.error('Portfolio API failed, falling back to reserve calculation:', error)
  }

  return []
}

export const fetchOptimizedCurrentAPYForSierra = async (
  serverApiBaseUrl: string,
  restAPIKey: string,
  poolAddr: EthAddr,
  accountAddr?: EthAddr
): Promise<string | null> => {
  try {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(endDate.getDate() - 7)

    const { portfolioCTOs } = await portfolioFetcherDaily(
      serverApiBaseUrl,
      restAPIKey,
      accountAddr,
      'MUSDC',
      startDate,
      endDate,
      0,
      10,
      poolAddr
    )

    if (portfolioCTOs && portfolioCTOs.length > 0) {
      const latestPortfolio = portfolioCTOs[portfolioCTOs.length - 1]
      const apy = Number(latestPortfolio.portfolioSummaryExcludingAccount.indicativeInterestRate) / 1e18
      return (apy * 100).toFixed(2)
    }
  } catch (error) {
    console.error('Portfolio API failed for current APY, falling back to reserve calculation:', error)
  }

  return null
}
